import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../css/About.css';

const FAQS = [
  {
    q: 'Who is The Elite Global Review for?',
    a: 'Our readership spans founders, executives, and business professionals who want to keep pace with market shifts, leadership thinking, and corporate strategy. We also work with companies looking for meaningful media exposure and PR features that go beyond a standard press release.',
  },
  {
    q: 'Which industries do you cover?',
    a: 'Our editorial calendar spans technology, finance, healthcare, manufacturing, real estate, retail, and sustainability, with regular coverage of emerging areas like AI, cybersecurity, and digital transformation.',
  },
  {
    q: 'How can I keep up with new stories and features?',
    a: 'Subscribe to our newsletter for curated industry insights and interviews delivered straight to your inbox, or follow us on social media for updates as they publish.',
  },
  {
    q: 'How can my company get featured?',
    a: 'We offer executive interviews, feature articles, sponsored content, and long-term brand partnerships. Reach out through our Contact page and our editorial team will help shape a story that fits your goals.',
  },
  {
    q: 'Can you build a custom feature or report for us?',
    a: 'Yes — our editorial and marketing teams regularly collaborate with organizations on tailored features and reports designed around a specific launch, milestone, or campaign. Get in touch with our media relations team to start the conversation.',
  },
  {
    q: 'How do I get in touch?',
    a: 'For editorial submissions, advertising, or partnership questions, visit our Contact page or email us directly — our team typically responds within one to two business days.',
  },
  {
    q: 'Do you offer advertising?',
    a: 'We do, including banner placements, sponsored content, newsletter features, and custom media packages built around your campaign objectives.',
  },
];

const STATS = [
  { end: 50, suffix: '+', label: 'Countries Reached' },
  { end: 300, suffix: '+', label: 'Leaders Featured' },
  { end: 1, suffix: 'M+', label: 'Monthly Readers' },
  { end: 15, suffix: '+', label: 'Industries Covered' },
];

/* Counts from 0 to `end` once the element scrolls into view */
function Counter({ end, suffix = '', duration = 2000 }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      setValue(end);
      return;
    }

    let frameId;

    const run = () => {
      const startTime = performance.now();
      const tick = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
        setValue(Math.round(eased * end));
        if (progress < 1) frameId = requestAnimationFrame(tick);
      };
      frameId = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          run();
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, [end, duration]);

  return (
    <span ref={ref} className="stat-number">
      {value}
      {suffix}
    </span>
  );
}

function FaqItem({ item, index, isOpen, onToggle }) {
  const buttonId = `faq-btn-${index}`;
  const panelId = `faq-panel-${index}`;

  return (
    <div className={`faq-item ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        id={buttonId}
        className="faq-question"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="faq-num">{String(index + 1).padStart(2, '0')}</span>
        <span className="faq-q-text">{item.q}</span>
        <span className="faq-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      <div
        className="faq-panel"
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
      >
        <div className="faq-panel-inner">
          <p className="faq-answer">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function AboutPage() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <main className="about-page">
      {/* Hero */}
      <section
        className="about-hero"
        style={{
          backgroundImage: "url('https://distant-black-tjwnlbax.edgeone.dev/file.png')",
        }}
      >
        <div className="about-hero-overlay" />
        <div className="wrap about-hero-inner">
          <p className="eyebrow">About</p>
          <h1>The Elite Global Review</h1>
          <span className="hero-divider" aria-hidden="true" />
          <p className="about-hero-sub">
            The Elite Global Review is a premier international business
            publication that recognizes <strong>visionary leaders</strong>,
            emerging innovators, and <strong>influential professionals</strong> who
            are making a meaningful impact across industries worldwide.
            Through in-depth cover stories, thought-provoking features, and
            exclusive insights, we celebrate leadership, innovation, and
            excellence on a global stage.
          </p>
        </div>
      </section>

      {/* Intro / mission */}
      <section className="about-section">
        <div className="wrap about-intro-grid">
          <div>
            <h2>Empowering Leaders, Shaping Success</h2>
            <p>
              The Elite Global Review gives businesses a platform to share their
              achievements, innovations, and impact on the industries they
              operate in. Through in-depth interviews, success stories, and
              strategic features, we put a spotlight on the leaders and
              organizations driving change — and help build the credibility
              that comes with it.
            </p>
            <p>
              We work primarily at the business-to-business level, connecting
              decision-makers, technical experts, and visionary companies.
              Whether you're a startup looking to establish a market presence
              or an established enterprise reinforcing your brand, our features
              are built to deliver meaningful exposure.
            </p>
          </div>
          <div className="about-highlight-card">
            <h3>Quality journalism. Meaningful storytelling.</h3>
            <p>
              Our coverage spans technology, finance, healthcare, retail, and
              beyond — giving professionals the context they need to navigate
              a fast-moving business landscape. More than a magazine, The Elite
              Global Review is a platform built to accelerate connections
              across industries.
            </p>
          </div>
        </div>
      </section>

      {/* Values / pillars */}
      <section className="about-section about-pillars-section">
        <div className="wrap">
          <h2>What We Stand For</h2>
          <div className="about-pillars">
            <div className="pillar">
              <span className="pillar-index">01</span>
              <h3>Credible Storytelling</h3>
              <p>
                Every feature is built on real conversations and researched
                context, not recycled press copy.
              </p>
            </div>
            <div className="pillar">
              <span className="pillar-index">02</span>
              <h3>Global Perspective</h3>
              <p>
                Our contributors and audience span North America, the Middle
                East, and Asia, giving every story a wider lens.
              </p>
            </div>
            <div className="pillar">
              <span className="pillar-index">03</span>
              <h3>Industry Depth</h3>
              <p>
                From fintech to sustainability, our editorial team covers the
                sectors shaping tomorrow's business landscape.
              </p>
            </div>
            <div className="pillar">
              <span className="pillar-index">04</span>
              <h3>Partnership First</h3>
              <p>
                We collaborate closely with the leaders and brands we feature
                to make sure every story lands the way it should.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="about-section about-stats-section">
        <div className="wrap about-stats">
          {STATS.map((s) => (
            <div className="stat" key={s.label}>
              <Counter end={s.end} suffix={s.suffix} />
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Industries covered */}
      <section className="about-section about-industries-section">
        <div className="wrap">
          <h2>Industries We Cover</h2>
          <p className="about-industries-sub">
            Our editorial calendar spans the sectors shaping today's business
            landscape — with a growing focus on the technologies and trends
            defining what comes next.
          </p>
          <div className="industry-tags">
            {[
              'Technology', 'Finance', 'Healthcare', 'Manufacturing',
              'Real Estate', 'Retail', 'Sustainability', 'Education',
              'Entertainment', 'Cybersecurity', 'Supply Chain', 'Energy',
            ].map((tag) => (
              <span key={tag} className="industry-tag">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="about-section about-faq-section">
        <div className="wrap faq-layout">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {FAQS.map((item, i) => (
              <FaqItem
                key={item.q}
                item={item}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* <section className="about-cta">
        <div className="wrap about-cta-inner">
          <div>
            <h2>Have a story worth telling?</h2>
            <p>Let's talk about how we can feature it.</p>
          </div>
          <Link to="/contact" className="about-cta-btn">Contact Us</Link>
        </div>
      </section> */}
    </main>
  );
}